// Shared hook for the `/api/fastapi/stats` endpoint. It powers both the
// empty-state corpus counts and the chat-input "LLM not configured" gating.
//
// Two requests, so the counts never wait for the LLM:
//   counts   `/stats?probe=0` answers from the nightly stats.json snapshot.
//            The last answer is kept in localStorage and painted at once on
//            the next visit, then refreshed in the background.
//   status   `/stats` streams one token from the LLM (up to 5 s, cached for a
//            minute server-side). Re-asked on window focus, so users returning
//            from /settings after fixing config see the input re-enable
//            without a full reload.
import { useCallback, useEffect, useState } from 'react';

export interface Stats {
  docs: number;
  actors: number;
  families: number;
  llm_status?: string;
}

export interface LlmStatusResult {
  stats: Stats | null;
  /** True until the first counts arrive (or are known to be unavailable). */
  statsPending: boolean;
  llmStatus: string | undefined;
  llmOk: boolean;
  refetch: () => void;
}

const CACHE_KEY = 'malpedia-corpus-stats';
const COUNTS_FRESH_MS = 5 * 60_000;

let countsFetchedAt = 0;

function readCached(): Stats | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<Stats>;
    if (typeof v.docs !== 'number') return null;
    return { docs: v.docs, actors: v.actors ?? 0, families: v.families ?? 0 };
  } catch {
    return null;
  }
}

function writeCached(s: Stats): void {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ docs: s.docs, actors: s.actors, families: s.families }),
    );
  } catch {
    /* private mode / quota — the cache is a convenience */
  }
}

function sameCounts(a: Stats | null, b: Stats): boolean {
  return !!a && a.docs === b.docs && a.actors === b.actors && a.families === b.families;
}

async function getJson(url: string, timeoutMs: number): Promise<Stats | null> {
  const r = await fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(timeoutMs) });
  return r.ok ? ((await r.json()) as Stats) : null;
}

export function useLlmStatus(): LlmStatusResult {
  const [counts, setCounts] = useState<Stats | null>(readCached);
  const [countsDone, setCountsDone] = useState(false);
  const [llmStatus, setLlmStatus] = useState<string | undefined>(undefined);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  const takeCounts = useCallback((data: Stats | null) => {
    if (!data || typeof data.docs !== 'number') return;
    const next = { docs: data.docs, actors: data.actors, families: data.families };
    setCounts((prev) => (sameCounts(prev, next) ? prev : next));
    writeCached(next);
    countsFetchedAt = Date.now();
  }, []);

  // Counts: fast, once per few minutes across remounts.
  useEffect(() => {
    let cancelled = false;
    if (Date.now() - countsFetchedAt < COUNTS_FRESH_MS && readCached()) {
      setCountsDone(true);
      return;
    }
    getJson('/api/fastapi/stats?probe=0', 8000)
      .then((data) => {
        if (!cancelled) takeCounts(data);
      })
      .catch(() => {
        /* best-effort */
      })
      .finally(() => {
        if (!cancelled) setCountsDone(true);
      });
    return () => {
      cancelled = true;
    };
  }, [takeCounts]);

  // LLM status: slow, re-asked on focus.
  useEffect(() => {
    let cancelled = false;
    getJson('/api/fastapi/stats', 9000)
      .then((data) => {
        if (cancelled || !data) return;
        setLlmStatus(data.llm_status);
        takeCounts(data);
      })
      .catch(() => {
        /* best-effort */
      });
    return () => {
      cancelled = true;
    };
  }, [tick, takeCounts]);

  useEffect(() => {
    const onFocus = () => refetch();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refetch]);

  const stats = counts ? { ...counts, llm_status: llmStatus } : null;
  return {
    stats,
    statsPending: !counts && !countsDone,
    llmStatus,
    llmOk: llmStatus === 'ok',
    refetch,
  };
}
